import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import { useResponsive } from '../../hook/useResponsive';
import { showApiError } from '../../services/apiError';
import { extractValues, updateUrlParams } from '../../validation/ValidationInput';
import ListFilterBar from './ListFilterBar';
import ListMobile from './ListMobile';
import ListTable from './ListTable';
import {
  Density,
  ListBulkAction,
  ListColumn,
  ListEmptyState,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListSortOption,
  PAGE_SIZES,
  SortDir,
  t,
} from './listShared';

export interface ListScreenHandle {
  /** Refetch with the current query (after a delete, a status change, ...). */
  reload: () => void;
  /** The rows currently shown. */
  rows: () => any[];
}

export interface ListScreenProps<Row = any> {
  language: any;
  columns: ListColumn<Row>[];
  /**
   * Loads one page. `search` is the unified box's raw text — map it to the
   * endpoint's param, or filter in memory and slice by page/limit yourself.
   */
  fetchRows: (query: ListQuery) => Promise<{ rows: Row[]; count: number }>;
  /**
   * The fetchRows result that was actually shown (a superseded response never
   * gets here), or null when the load failed: set page totals or tiles here.
   */
  onLoaded?: (result: any | null) => void;
  rowKey?: (row: Row) => string;
  /** i18n key. Omit `searchPlaceholder` AND pass `search={false}` to hide the box. */
  search?: boolean;
  searchPlaceholder?: string;
  filters?: ListFilter[];
  /**
   * Explicit sort options; otherwise derived from the sortable columns. The
   * Sort control only renders when the result is non-empty.
   */
  sortOptions?: ListSortOption[];
  defaultSort?: { sortBy: string; sortDir: SortDir };
  bulkActions?: ListBulkAction[];
  /** Desktop kebab entries; `mobileRowActions` defaults to the same list. */
  rowActions?: ListRowAction<Row>[];
  mobileRowActions?: ListRowAction<Row>[];
  recordHeader?: (row: Row) => React.ReactNode;
  countNoun?: string;
  emptyState: ListEmptyState;
  /** URL param name per filter key (defaults to the key itself). */
  urlKeys?: Record<string, string>;
  /**
   * Extra query-string params the screen owns outside the list (e.g. a `tab`
   * the page switches with `key={tab}`); written on every URL update so they
   * survive paging / searching. Empty values are dropped.
   */
  extraUrlParams?: Record<string, string>;
  searchInputRef?: React.RefObject<HTMLInputElement>;
}

/**
 * Wires a list screen end to end: query state, URL persistence, the 700ms
 * search debounce, selection (cleared on every refetch) and the lg (1024px)
 * desktop / mobile switch. The page renders its title row above this and
 * owns the dialogs its actions open.
 *
 * Put it inside a `display: flex; flex-direction: column` container on
 * desktop so the table fills the remaining height.
 */
const ListScreen = forwardRef<ListScreenHandle, ListScreenProps>(
  (
    {
      language,
      columns,
      fetchRows,
      rowKey = (row: any) => String(row?.id),
      search = true,
      searchPlaceholder = 'Search',
      filters = [],
      sortOptions,
      defaultSort,
      bulkActions,
      rowActions = [],
      mobileRowActions,
      recordHeader,
      countNoun,
      emptyState,
      onLoaded,
      urlKeys = {},
      extraUrlParams,
      searchInputRef,
    },
    ref
  ) => {
    const { isTouchLayout: isMobile } = useResponsive();
    const hasSort = !!defaultSort || (sortOptions?.length ?? 0) > 0 || columns.some(c => c.sortable);
    const emptyFilters = filters.reduce((acc: Record<string, string>, f) => {
      acc[f.key] = '';
      return acc;
    }, {});
    const initialQuery: ListQuery = {
      page: 1,
      limit: 50,
      search: '',
      filters: emptyFilters,
      sortBy: defaultSort?.sortBy ?? '',
      sortDir: defaultSort?.sortDir ?? 'DESC',
    };

    const [query, setQuery] = useState<ListQuery>(initialQuery);
    const [rows, setRows] = useState<any[]>([]);
    const [count, setCount] = useState<number>(0);
    // True from the first paint: the mount effect always loads, so "nothing yet" never flashes.
    const [loading, setLoading] = useState<boolean>(true);
    const [failed, setFailed] = useState<boolean>(false);
    /** True while the last change was typed into the search box (debounced). */
    const [typing, setTyping] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectMode, setSelectMode] = useState<boolean>(false);
    const [density, setDensity] = useState<Density>('default');
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
    const latestQuery = useRef<ListQuery>(initialQuery);
    const requestSeq = useRef<number>(0);
    /** The query the current rows were fetched with (drives the no-results state). */
    const [loadedQuery, setLoadedQuery] = useState<ListQuery | null>(null);
    /** Always the latest fetcher, so the mount-time listeners never call a stale closure. */
    const fetchRef = useRef(fetchRows);
    fetchRef.current = fetchRows;

    const load = async (next: ListQuery) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      // Every refetch invalidates the selection.
      setSelectedIds([]);
      setSelectMode(false);
      try {
        const result = await fetchRef.current(next);
        if (seq !== requestSeq.current) return;
        const total = result.count ?? 0;
        const lastPage = Math.max(1, Math.ceil(total / (next.limit || 50)));
        if (total > 0 && next.page > lastPage) {
          // e.g. the last row of the last page was just deleted: fall back
          // to the last page that still has rows instead of showing nothing.
          const clamped = { ...next, page: lastPage };
          latestQuery.current = clamped;
          setQuery(clamped);
          window.history.replaceState(window.history.state, '', urlFor(clamped));
          void load(clamped);
          return;
        }
        setRows(result.rows ?? []);
        setCount(total);
        setLoadedQuery(next);
        setFailed(false);
        onLoaded?.(result);
      } catch (error) {
        console.error('Error loading list:', error);
        if (seq !== requestSeq.current) return;
        setRows([]);
        setCount(0);
        setFailed(true);
        onLoaded?.(null);
        showApiError(error, { language });
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      reload: () => load(latestQuery.current),
      rows: () => rows,
    }));

    /**
     * Push the whole list state into the query string so Back, refresh and a
     * shared link all restore the same list. updateUrlParams percent-encodes,
     * extractValues decodes, so free text round-trips.
     */
    const urlFor = (next: ListQuery) => {
      const extra: Record<string, any> = { ...(extraUrlParams ?? {}) };
      filters.forEach(f => {
        extra[urlKeys[f.key] ?? f.key] = next.filters[f.key];
      });
      if (hasSort) {
        extra.sortBy = next.sortBy;
        extra.sortDir = next.sortDir;
      }
      return updateUrlParams(
        { searchValue: next.search, page: next.page, limit: next.limit },
        extra
      );
    };

    /**
     * Chips, sort, page and limit replace the URL in place: the list reads it
     * only on mount, so pushed entries made Back change the address bar alone.
     */
    const apply = (next: ListQuery) => {
      latestQuery.current = next;
      setTyping(false);
      setQuery(next);
      window.history.replaceState(window.history.state, '', location.pathname + urlFor(next));
      load(next);
    };

    /** Search typing also replaces the entry; the debounced effect below does the fetch. */
    const changeSearch = (value: string) => {
      const next = { ...latestQuery.current, search: value, page: 1 };
      latestQuery.current = next;
      setTyping(true);
      setQuery(next);
      window.history.replaceState(window.history.state, '', location.pathname + urlFor(next));
    };

    useDebounceEffect(
      () => {
        if (typing) load(latestQuery.current);
      },
      [query],
      700
    );

    useEffect(() => {
      const params: any = extractValues(location.search.split('&'));
      const parsedLimit = +params.limit;
      const restoredFilters = { ...emptyFilters };
      filters.forEach(f => {
        restoredFilters[f.key] = params[urlKeys[f.key] ?? f.key] ?? '';
      });
      const restored: ListQuery = {
        ...initialQuery,
        search: params.search ?? '',
        page: +params.page || 1,
        limit: PAGE_SIZES.includes(parsedLimit) ? parsedLimit : initialQuery.limit,
        filters: restoredFilters,
        sortBy: hasSort ? params.sortBy || initialQuery.sortBy : '',
        sortDir: params.sortDir === 'ASC' || params.sortDir === 'DESC' ? params.sortDir : initialQuery.sortDir,
      };
      latestQuery.current = restored;
      setQuery(restored);
      load(restored);

      const handleNewToken = () => load(latestQuery.current);
      document.addEventListener('newToken', handleNewToken);
      return () => document.removeEventListener('newToken', handleNewToken);
    }, []);

    const changeFilter = (key: string, value: string) =>
      apply({ ...latestQuery.current, filters: { ...latestQuery.current.filters, [key]: value }, page: 1 });
    const clearFilters = () => apply({ ...latestQuery.current, filters: { ...emptyFilters }, page: 1 });
    const changePage = (page: number) => apply({ ...latestQuery.current, page });
    const changeLimit = (limit: number) => apply({ ...latestQuery.current, limit, page: 1 });
    const changeSort = (sortBy: string, sortDir: SortDir) =>
      apply({ ...latestQuery.current, sortBy, sortDir, page: 1 });

    const toggleRow = (id: string) =>
      setSelectedIds(prev => (prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]));
    const togglePage = () => {
      const pageIds = rows.map(rowKey);
      const allSelected =
        pageIds.length > 0 && pageIds.every((id: string) => selectedIds.includes(id));
      setSelectedIds(allSelected ? [] : pageIds);
    };

    const searchProps = search
      ? { searchValue: query.search, onSearchChange: changeSearch, searchPlaceholder }
      : {};
    const sortProps = hasSort
      ? { sortBy: query.sortBy, sortDir: query.sortDir, onSortChange: changeSort }
      : {};
    const selectable = (bulkActions?.length ?? 0) > 0;
    /** A search or filter is active, so an empty result is "no matches", not "nothing yet". */
    // Judged on the query the rows came from, not the live input: clearing
    // the search must not flash the "nothing yet" state for the 700ms until
    // the refetch lands.
    const judged = loadedQuery ?? query;
    const hasActiveQuery =
      !failed && (judged.search.trim() !== '' || Object.values(judged.filters).some(value => !!value));
    // A failed load says so instead of showing the "nothing here yet" state.
    const shownEmptyState: ListEmptyState = failed
      ? {
          title: t(language, 'Could not load this list', 'Could not load this list'),
          desc: t(language, 'Check your connection and try again.', 'Check your connection and try again.'),
        }
      : emptyState;

    if (isMobile) {
      return (
        <div style={{ marginTop: '20px' }}>
          <ListMobile
            language={language}
            columns={columns}
            rows={rows}
            rowKey={rowKey}
            loading={loading}
            count={count}
            page={query.page}
            limit={query.limit}
            onPageChange={changePage}
            {...searchProps}
            filters={filters}
            filterValues={query.filters}
            onFilterChange={changeFilter}
            onClearFilters={clearFilters}
            sortOptions={sortOptions}
            {...sortProps}
            selection={
              selectable
                ? {
                    selectMode,
                    onEnterSelectMode: (id: string) => {
                      setSelectMode(true);
                      setSelectedIds([id]);
                    },
                    onExitSelectMode: () => {
                      setSelectMode(false);
                      setSelectedIds([]);
                    },
                    selectedIds,
                    onToggleRow: toggleRow,
                    onSelectAllPage: togglePage,
                    bulkActions,
                  }
                : undefined
            }
            rowActions={mobileRowActions ?? rowActions}
            recordHeader={recordHeader}
            countNoun={countNoun}
            emptyState={shownEmptyState}
            hasActiveQuery={hasActiveQuery}
          />
        </div>
      );
    }

    return (
      <div style={{ marginTop: '24px', flex: 1, minHeight: 0 }}>
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            minHeight: 0,
          }}
        >
          <ListFilterBar
            language={language}
            {...searchProps}
            searchInputRef={searchInputRef}
            filters={filters}
            filterValues={query.filters}
            onFilterChange={changeFilter}
            columns={columns}
            sortOptions={sortOptions}
            {...sortProps}
            visibleColumns={visibleColumns}
            onToggleColumn={(key: string) =>
              setVisibleColumns(prev => ({ ...prev, [key]: prev[key] === false }))
            }
            density={density}
            onDensityChange={setDensity}
          />
          <div style={{ flex: 1, minHeight: 0 }}>
            <ListTable
              language={language}
              columns={columns}
              rows={rows}
              rowKey={rowKey}
              loading={loading}
              count={count}
              page={query.page}
              limit={query.limit}
              onPageChange={changePage}
              onLimitChange={changeLimit}
              {...sortProps}
              selection={
                selectable
                  ? {
                      selectedIds,
                      onToggleRow: toggleRow,
                      onTogglePage: togglePage,
                      onClearSelection: () => setSelectedIds([]),
                      bulkActions,
                    }
                  : undefined
              }
              rowActions={rowActions}
              emptyState={shownEmptyState}
              hasActiveQuery={hasActiveQuery}
              visibleColumns={visibleColumns}
              density={density}
            />
          </div>
        </div>
      </div>
    );
  }
);

ListScreen.displayName = 'ListScreen';

export default ListScreen;
